import {
  adminServerDetailSchema,
  importFinishSchema,
  importReportSchema,
  type ImportCheckResult,
  type ImportFix,
  type ImportReport,
} from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  AlertTriangle,
  CheckCircle2,
  FileArchive,
  Import,
  Loader2,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { CopyChip } from '@/components/copy-chip';
import { PageHeader } from '@/components/page-header';
import { QueryState } from '@/components/query-state';
import { ServerCreateForm } from '@/components/server-create-form';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { apiRequest } from '@/lib/api';
import { bytes } from '@/lib/format';
import { errorMessage } from '@/lib/forms';
import { serverQuery } from '@/lib/server-queries';
import { uploads, useUploads } from '@/lib/uploads';

export const Route = createFileRoute('/_app/admin/servers/import')({
  validateSearch: (s: Record<string, unknown>): { server?: number } => ({
    server: Number(s.server) > 0 ? Number(s.server) : undefined,
  }),
  component: ImportServer,
});

/** What hearthd can extract; anything else is refused before uploading. */
const ARCHIVES = ['.zip', '.tar', '.tar.gz', '.tgz', '.tar.xz', '.txz'];

function ImportServer() {
  const { server } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  return (
    <>
      <PageHeader
        title="Import a server"
        icon={Import}
        description="Move a game server from another host: create it here, bring its files in, then check them."
      />
      <div className="grid gap-6">
        <WhatWorks />
        {server ? (
          <ImportSteps id={server} />
        ) : (
          <>
            <StepTitle n={1} title="Create the server" />
            <ServerCreateForm
              mode="import"
              onCreated={(s) => navigate({ search: { server: s.id } })}
            />
          </>
        )}
      </div>
    </>
  );
}

function WhatWorks() {
  return (
    <Card>
      <CardContent className="grid gap-4 text-sm sm:grid-cols-2">
        <div>
          <p className="mb-1 font-medium">Supported</p>
          <ul className="text-muted-foreground list-disc space-y-1 pl-5">
            <li>Servers that are plain files: the jar or binary, configs and world folders.</li>
            <li>Archives in zip, tar, tar.gz (or .tgz) and tar.xz, or copying over SFTP.</li>
          </ul>
        </div>
        <div>
          <p className="mb-1 font-medium">Not supported</p>
          <ul className="text-muted-foreground list-disc space-y-1 pl-5">
            <li>Pulling a server from another host’s panel through its API.</li>
            <li>
              Databases: create one on the server’s Databases page and load your dump yourself.
            </li>
            <li>
              Modpack manifests (CurseForge, Modrinth): upload the pack’s server files instead.
            </li>
            <li>7z and rar archives.</li>
            <li>Servers that need a different Java version than the egg’s images provide.</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}

function StepTitle({ n, title, done }: { n: number; title: string; done?: boolean }) {
  return (
    <h2 className="flex items-center gap-2 text-lg font-semibold">
      <span
        className="bg-muted text-muted-foreground flex size-7 items-center justify-center rounded-full text-sm"
        aria-hidden
      >
        {done ? <CheckCircle2 className="text-success size-4" /> : n}
      </span>
      {title}
    </h2>
  );
}

function ImportSteps({ id }: { id: number }) {
  const detail = useQuery({
    queryKey: ['admin', 'servers', id],
    queryFn: ({ signal }) =>
      apiRequest(`/api/application/servers/${id}`, { schema: adminServerDetailSchema, signal }),
    // The node marks a script-less server installed almost at once; follow it until then.
    refetchInterval: (q) => (q.state.data?.status === 'installing' ? 2000 : false),
  });
  return (
    <QueryState query={detail}>
      {(server) => (
        <>
          <StepTitle n={1} title={`Created “${server.name}” on ${server.node.name}`} done />
          {server.status === 'installing' ? (
            <p className="text-muted-foreground flex items-center gap-2 text-sm">
              <Loader2 className="size-4 animate-spin" /> Preparing the server folder on the node…
            </p>
          ) : server.status && server.status !== 'install_failed' ? (
            <Alert variant="destructive">
              <AlertTriangle />
              <AlertTitle>Can’t import right now</AlertTitle>
              <AlertDescription>
                The server is {server.status.replace('_', ' ')}. Open its{' '}
                <Link
                  to="/admin/servers/$serverId"
                  params={{ serverId: String(server.id) }}
                  className="underline"
                >
                  admin page
                </Link>{' '}
                to sort that out first.
              </AlertDescription>
            </Alert>
          ) : (
            <>
              <StepTitle n={2} title="Bring the files in" />
              <BringFiles identifier={server.identifier} serverId={server.id} />
              <StepTitle n={3} title="Check and finish" />
              <CheckAndFinish id={server.id} identifier={server.identifier} />
            </>
          )}
        </>
      )}
    </QueryState>
  );
}

type Phase =
  | { kind: 'idle' }
  | { kind: 'uploading'; name: string }
  | { kind: 'extracting'; name: string }
  | { kind: 'done'; name: string }
  | { kind: 'error'; message: string };

function BringFiles({ identifier, serverId }: { identifier: string; serverId: number }) {
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const items = useUploads(identifier);
  const current =
    phase.kind === 'uploading' ? items.findLast((i) => i.name === phase.name) : undefined;
  const sftp = useQuery(serverQuery(identifier));

  const files = (op: string, body: unknown) =>
    apiRequest(`/api/client/servers/${identifier}/files/${op}`, {
      method: 'POST',
      body,
      schema: z.unknown(),
    });

  const extract = async (name: string) => {
    setPhase({ kind: 'extracting', name });
    try {
      await files('decompress', { root: '/', file: name });
      // The archive has served its purpose and would count against the disk limit.
      await files('delete', { root: '/', files: [name] });
      setPhase({ kind: 'done', name });
    } catch (err) {
      setPhase({ kind: 'error', message: `Couldn’t extract ${name}: ${errorMessage(err)}` });
    }
    await queryClient.invalidateQueries({ queryKey: ['admin', 'servers', serverId, 'import'] });
  };

  const choose = (file: File | undefined) => {
    if (input.current) input.current.value = '';
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!ARCHIVES.some((ext) => lower.endsWith(ext))) {
      setPhase({
        kind: 'error',
        message: `${file.name} isn’t a zip or tar archive. 7z and rar can’t be extracted here: extract it on your computer and upload a zip, or copy the files over SFTP.`,
      });
      return;
    }
    setPhase({ kind: 'uploading', name: file.name });
    uploads.add(identifier, '/', [file], () => {
      const item = uploads
        .snapshot()
        .findLast((i) => i.server === identifier && i.name === file.name);
      if (item?.status === 'done') void extract(file.name);
      else if (item?.status === 'cancelled') setPhase({ kind: 'idle' });
      else setPhase({ kind: 'error', message: item?.error ?? 'The upload failed.' });
    });
  };

  const busy = phase.kind === 'uploading' || phase.kind === 'extracting';
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>Upload an archive</CardTitle>
          <CardDescription>
            A zip or tar of the old server’s folder. It is uploaded to the node in chunks, extracted
            into the server folder, and then deleted.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3">
          <input
            ref={input}
            type="file"
            accept={ARCHIVES.join(',')}
            className="hidden"
            aria-label="Archive to import"
            onChange={(e) => choose(e.target.files?.[0])}
          />
          <div>
            <Button type="button" onClick={() => input.current?.click()} disabled={busy}>
              <FileArchive /> Choose archive
            </Button>
          </div>
          {phase.kind === 'uploading' && (
            <p className="text-muted-foreground flex items-center gap-2 text-sm" role="status">
              <Loader2 className="size-4 animate-spin" /> Uploading {phase.name}
              {current ? `: ${bytes(current.sent)} of ${bytes(current.size)}` : '…'}
              {current && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => uploads.cancel(current.id)}
                >
                  Cancel
                </Button>
              )}
            </p>
          )}
          {phase.kind === 'extracting' && (
            <p className="text-muted-foreground flex items-center gap-2 text-sm" role="status">
              <Loader2 className="size-4 animate-spin" /> Extracting {phase.name}…
            </p>
          )}
          {phase.kind === 'done' && (
            <p className="text-success flex items-center gap-2 text-sm" role="status">
              <CheckCircle2 className="size-4" /> Extracted {phase.name}. Check the files below.
            </p>
          )}
          {phase.kind === 'error' && (
            <p className="text-destructive text-sm" role="alert">
              {phase.message}
            </p>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Or copy over SFTP</CardTitle>
          <CardDescription>
            Put the files at the top of the server folder (where the server’s jar or binary goes),
            not inside a subfolder.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 text-sm">
          <QueryState query={sftp}>
            {(s) => (
              <>
                <CopyChip
                  value={`sftp -P ${s.sftp.port} ${s.sftp.username}@${s.sftp.host}`}
                  label="SFTP command"
                  className="w-fit"
                />
                <p className="text-muted-foreground">
                  Sign in with your panel password or an{' '}
                  <Link to="/account/ssh-keys" className="underline">
                    SSH key
                  </Link>
                  . The owner can also connect with their own username. You can also use the{' '}
                  <Link
                    to="/server/$server/files"
                    params={{ server: identifier }}
                    className="underline"
                  >
                    file manager
                  </Link>
                  .
                </p>
              </>
            )}
          </QueryState>
        </CardContent>
      </Card>
    </div>
  );
}

const fixLabel = (fix: ImportFix) =>
  fix.kind === 'use-jar' ? `Use ${fix.file}` : `Move files out of ${fix.folder}`;

function CheckAndFinish({ id, identifier }: { id: number; identifier: string }) {
  const queryClient = useQueryClient();
  const key = ['admin', 'servers', id, 'import'];
  const report = useQuery({
    queryKey: key,
    queryFn: ({ signal }) =>
      apiRequest(`/api/application/servers/${id}/import`, { schema: importReportSchema, signal }),
  });
  const [finished, setFinished] = useState<ImportReport | null>(null);

  const fix = useMutation({
    mutationFn: (body: ImportFix) =>
      apiRequest(`/api/application/servers/${id}/import/fix`, {
        method: 'POST',
        body,
        schema: importReportSchema,
      }),
    onSuccess: (r) => {
      queryClient.setQueryData(key, r);
      toast.success('Done. The files were checked again.');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const finish = useMutation({
    mutationFn: () =>
      apiRequest(`/api/application/servers/${id}/import/finish`, {
        method: 'POST',
        schema: importFinishSchema,
      }),
    onSuccess: async (r) => {
      queryClient.setQueryData(key, r.report);
      if (!r.finished) {
        toast.error('A check fails now. Fix it and try again.');
        return;
      }
      setFinished(r.report);
      if (!r.daemonSynced)
        toast.warning(`Imported, but the node couldn’t be updated: ${r.daemonError ?? ''}`);
      await queryClient.invalidateQueries({ queryKey: ['admin', 'servers'] });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (finished) {
    const warnings = finished.checks.filter((c) => c.status === 'warn').length;
    return (
      <Alert>
        <CheckCircle2 className="text-success" />
        <AlertTitle>Import finished</AlertTitle>
        <AlertDescription>
          <p>
            The import is recorded in the server’s activity
            {warnings
              ? `, with ${warnings} warning${warnings === 1 ? '' : 's'} to keep in mind`
              : ''}
            . Start it from the console when you’re ready.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button asChild size="sm">
              <Link to="/server/$server" params={{ server: identifier }}>
                Open the console
              </Link>
            </Button>
            <Button asChild size="sm" variant="outline">
              <Link to="/admin/servers/$serverId" params={{ serverId: String(id) }}>
                Admin page
              </Link>
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div className="grid gap-1.5">
          <CardTitle>What’s in the server folder</CardTitle>
          <CardDescription>
            {report.data
              ? `Checked as ${report.data.profile.identity.name}. Failures must be fixed before finishing; warnings are worth knowing but don’t block it.`
              : 'Each check says what it found and what to do.'}
          </CardDescription>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void report.refetch()}
          disabled={report.isFetching}
        >
          <RefreshCw className={report.isFetching ? 'animate-spin' : undefined} /> Check again
        </Button>
      </CardHeader>
      <CardContent className="grid gap-4">
        <QueryState query={report}>
          {(r) => (
            <>
              <ul className="divide-y rounded-md border" aria-label="Import checks">
                {r.checks.map((c) => (
                  <CheckRow key={c.id} check={c}>
                    {c.fix && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={fix.isPending}
                        onClick={() => fix.mutate(c.fix!)}
                      >
                        {fix.isPending && <Loader2 className="animate-spin" />} {fixLabel(c.fix)}
                      </Button>
                    )}
                  </CheckRow>
                ))}
              </ul>
              <div className="flex flex-wrap items-center justify-end gap-3">
                {!r.ready && (
                  <p className="text-muted-foreground text-sm">Fix the failed checks to finish.</p>
                )}
                <Button disabled={!r.ready || finish.isPending} onClick={() => finish.mutate()}>
                  {finish.isPending && <Loader2 className="animate-spin" />} Finish import
                </Button>
              </div>
            </>
          )}
        </QueryState>
      </CardContent>
    </Card>
  );
}

const STATUS = {
  pass: { icon: CheckCircle2, className: 'text-success', label: 'Passed' },
  warn: { icon: AlertTriangle, className: 'text-warning', label: 'Warning' },
  fail: { icon: XCircle, className: 'text-destructive', label: 'Failed' },
} as const;

function CheckRow({ check, children }: { check: ImportCheckResult; children?: ReactNode }) {
  const s = STATUS[check.status];
  return (
    <li className="flex flex-wrap items-start gap-3 p-3 text-sm">
      <s.icon className={`mt-0.5 size-4 shrink-0 ${s.className}`} aria-label={s.label} />
      <div className="grid min-w-0 flex-1 gap-0.5">
        <p className="font-medium">{check.label}</p>
        <p className="text-muted-foreground break-words">{check.detail}</p>
        {check.action && <p className="break-words">{check.action}</p>}
      </div>
      {children}
    </li>
  );
}
